import { useState } from "react";
import { contactFAQs } from "@/mocks/contactData";
import Icon from "@/components/feature/Icon";
import { supabase } from "@/lib/supabase";
import { useSiteSettings } from "@/lib/siteSettings";
import { usePublicRows } from "@/hooks/usePublicContent";
import type { DbFaq } from "@/types/database";

/** Legacy Readdy form endpoint — used only if the database insert fails. */
const LEGACY_FORM_ENDPOINT = "https://readdy.ai/api/form/d87thgjrqig5fimrcqog";

const FALLBACK_FAQS: DbFaq[] = contactFAQs.map((f, i) => ({
  id: -(i + 1),
  page: "contact",
  question: f.question,
  answer: f.answer,
  sort_order: i + 1,
  is_active: true,
  created_at: "",
  updated_at: "",
}));

export default function ContactInfoSection() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { settings } = useSiteSettings();
  const contact = settings.contact;
  const { rows: faqs } = usePublicRows<DbFaq>("faqs", FALLBACK_FAQS, { refine: (q) => q.eq("page", "contact") });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const payload = {
      name: String(formData.get("name") || "").trim(),
      email: String(formData.get("email") || "").trim(),
      phone: String(formData.get("phone") || "").trim() || null,
      organization: String(formData.get("organization") || "").trim() || null,
      subject: String(formData.get("subject") || "other"),
      message: String(formData.get("message") || "").trim(),
      newsletter: formData.get("newsletter") === "on",
      status: "new",
    };

    setSubmitting(true);
    setSubmitError(null);
    try {
      const { error } = await supabase.from("contact_messages").insert([payload]);
      if (error) throw error;
      setSubmitted(true);
      form.reset();
    } catch {
      // Database unavailable (e.g. not migrated yet): fall back to the legacy form service.
      try {
        const res = await fetch(LEGACY_FORM_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams(formData as never).toString(),
        });
        if (!res.ok) throw new Error(String(res.status));
        setSubmitted(true);
        form.reset();
      } catch {
        setSubmitError(`訊息送出失敗，請稍後再試，或直接來信 ${contact.email}。`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const infoCards = [
    {
      icon: "ri-map-pin-line",
      label: "協會會址",
      value: contact.address,
      action: "查看地圖",
      actionHref: "#map-section",
      external: false,
    },
    {
      icon: "ri-mail-line",
      label: "電子郵件",
      value: contact.email,
      action: "寄送郵件",
      actionHref: `mailto:${contact.email}`,
      external: false,
    },
    contact.phone
      ? {
          icon: "ri-phone-line",
          label: "聯絡電話",
          value: contact.phone,
          action: "撥打電話",
          actionHref: `tel:${contact.phone.replace(/[^\d+]/g, "")}`,
          external: false,
        }
      : {
          icon: "ri-chat-1-fill",
          label: "LINE 社群",
          value: contact.community_url ? "加入協會 LINE 社群，接收活動通知與交流訊息" : "協會 LINE 社群建置中",
          action: contact.community_url ? "加入社群" : "",
          actionHref: contact.community_url,
          external: true,
        },
  ];

  return (
    <section className="pt-12 pb-16 md:pt-16 md:pb-24 bg-white">
      <div className="px-6 md:px-10 lg:px-16">
        {/* Section header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
            Contact Information
          </p>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-semibold text-navy-950 mb-4">
            聯絡資訊與表單
          </h2>
          <p className="text-silver-500 text-base md:text-lg leading-relaxed">
            選擇最適合您的方式與我們聯繫，或直接填寫表單，我們將盡快回覆。
          </p>
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-12 md:mb-20">
          {infoCards.map((card) => (
            <div
              key={card.label}
              className="group bg-silver-50 border border-silver-200 rounded-xl p-5 md:p-6 hover:border-navy-300 transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-lg bg-navy-950 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <Icon className={`${card.icon} text-white text-lg`} />
              </div>
              <p className="text-xs text-silver-500 uppercase tracking-wider mb-1.5">
                {card.label}
              </p>
              <p className="text-navy-950 font-medium text-sm md:text-base mb-3 leading-snug">
                {card.value}
              </p>
              {card.action && card.actionHref && (
                <a
                  href={card.actionHref}
                  {...(card.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="inline-flex items-center gap-1.5 text-navy-800 text-sm font-medium hover:text-navy-950 transition-colors"
                >
                  {card.action}
                  <Icon className="ri-arrow-right-line text-xs" />
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Map + Form */}
        <div id="map-section" className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Left: Map + FAQ */}
          <div className="space-y-6">
            {contact.map_embed_url && (
              <div className="rounded-xl overflow-hidden border border-silver-200 h-[320px] md:h-[420px] bg-silver-50">
                <iframe
                  src={contact.map_embed_url}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="TAIAA 協會地圖"
                  className="grayscale-[20%] hover:grayscale-0 transition-all duration-500"
                />
              </div>
            )}

            {/* FAQ */}
            {faqs.length > 0 && (
              <div className="bg-silver-50 border border-silver-200 rounded-xl p-5 md:p-6">
                <h3 className="text-navy-950 font-semibold text-base md:text-lg mb-4 flex items-center gap-2">
                  <Icon className="ri-question-line text-navy-700" />
                  常見問題
                </h3>
                <div className="space-y-2">
                  {faqs.map((faq, idx) => (
                    <div
                      key={faq.id}
                      className="border border-silver-200 rounded-lg overflow-hidden bg-white"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                        className="w-full flex items-center justify-between p-3.5 md:p-4 text-left hover:bg-silver-50 transition-colors"
                      >
                        <span className="text-navy-950 font-medium text-sm pr-4">
                          {faq.question}
                        </span>
                        <Icon
                          className={`ri-${openFaq === idx ? "subtract" : "add"}-line text-navy-700 flex-shrink-0`}
                        />
                      </button>
                      {openFaq === idx && (
                        <div className="px-3.5 md:px-4 pb-3.5 md:pb-4">
                          <p className="text-silver-500 text-sm leading-relaxed whitespace-pre-line">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Form */}
          <div className="bg-silver-50 border border-silver-200 rounded-xl p-5 md:p-8">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 rounded-full bg-navy-950 flex items-center justify-center mb-5">
                  <Icon className="ri-check-line text-white text-2xl" />
                </div>
                <h3 className="font-serif text-xl md:text-2xl font-semibold text-navy-950 mb-3">
                  訊息已送出
                </h3>
                <p className="text-silver-500 text-base max-w-sm leading-relaxed mb-6">
                  感謝您的來信！我們已收到您的訊息，將在工作日 24 小時內與您聯繫。
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="inline-flex items-center gap-2 bg-navy-950 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-navy-900 transition-colors whitespace-nowrap"
                >
                  <Icon className="ri-restart-line" />
                  再寄一封
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <h3 className="text-navy-950 font-semibold text-base md:text-lg mb-1">
                    聯絡表單
                  </h3>
                  <p className="text-silver-500 text-sm mb-5">
                    請填寫以下資訊，我們將盡快與您聯繫。
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-navy-950 mb-1.5">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      maxLength={100}
                      placeholder="請輸入您的姓名"
                      className="w-full px-4 py-3 rounded-lg border border-silver-300 bg-white text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 focus:ring-1 focus:ring-navy-700 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy-950 mb-1.5">
                      聯絡電話
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      maxLength={50}
                      placeholder="請輸入聯絡電話"
                      className="w-full px-4 py-3 rounded-lg border border-silver-300 bg-white text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 focus:ring-1 focus:ring-navy-700 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-950 mb-1.5">
                    電子郵件 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    maxLength={200}
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 rounded-lg border border-silver-300 bg-white text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 focus:ring-1 focus:ring-navy-700 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-950 mb-1.5">
                    單位 / 組織名稱
                  </label>
                  <input
                    type="text"
                    name="organization"
                    maxLength={200}
                    placeholder="請輸入您的單位或組織名稱"
                    className="w-full px-4 py-3 rounded-lg border border-silver-300 bg-white text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 focus:ring-1 focus:ring-navy-700 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-950 mb-1.5">
                    聯絡主旨 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="subject"
                    required
                    defaultValue=""
                    className="w-full px-4 py-3 rounded-lg border border-silver-300 bg-white text-sm text-navy-950 focus:outline-none focus:border-navy-700 focus:ring-1 focus:ring-navy-700 transition-colors appearance-none"
                  >
                    <option value="" disabled>
                      請選擇聯絡主旨
                    </option>
                    <option value="general">一般諮詢</option>
                    <option value="membership">入會與會員問題</option>
                    <option value="event">活動報名與合作</option>
                    <option value="partnership">企業合作與贊助</option>
                    <option value="media">媒體採訪</option>
                    <option value="other">其他</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-950 mb-1.5">
                    訊息內容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="請描述您的需求或問題..."
                    className="w-full px-4 py-3 rounded-lg border border-silver-300 bg-white text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 focus:ring-1 focus:ring-navy-700 transition-colors resize-none"
                  />
                  <p className="text-silver-400 text-xs mt-1 text-right">
                    最多 500 字
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="newsletter"
                    id="newsletter"
                    className="mt-0.5 w-4 h-4 rounded border-silver-300 text-navy-950 focus:ring-navy-700"
                  />
                  <label
                    htmlFor="newsletter"
                    className="text-sm text-silver-500 leading-relaxed cursor-pointer"
                  >
                    我願意訂閱 TAIAA 電子報，接收最新活動與產業洞察。
                  </label>
                </div>

                {submitError && (
                  <p className="text-sm text-red-600 flex items-center gap-2" role="alert">
                    <Icon className="ri-error-warning-line" />
                    {submitError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-navy-950 text-white py-3.5 rounded-full text-sm font-medium hover:bg-navy-900 transition-colors flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? <Icon className="ri-loader-4-line animate-spin" /> : <Icon className="ri-send-plane-line" />}
                  {submitting ? "送出中..." : "送出訊息"}
                </button>
                <p className="text-center text-silver-400 text-xs">
                  也歡迎直接來信{" "}
                  <a href={`mailto:${contact.email}`} className="hover:text-navy-800 underline-offset-2 hover:underline">
                    {contact.email}
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
